import { cn } from "@istari-frontend/shared";
import { Avatar, AvatarFallback, Button, Label } from "@istari-frontend/ui";
import { Command as CommandPrimitive } from "cmdk";
import { Globe, Plus } from "lucide-react";
import { type ReactNode, useId, useRef, useState } from "react";

import { ShareableSendingConnection } from "~/secure-connections/model/secure-connection";

type ConnectionSearchInputProps = {
  availableConnections: ShareableSendingConnection[];
  onSelectConnection: (connection: ShareableSendingConnection) => void;
  isLoading?: boolean;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  labelExtra?: ReactNode;
};

export function ConnectionSearchInput({
  availableConnections,
  onSelectConnection,
  isLoading = false,
  placeholder = "Search external connections...",
  label = "Add organization",
  disabled = false,
  labelExtra,
}: ConnectionSearchInputProps) {
  const searchInputId = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const term = search.trim().toLowerCase();
  const filteredConnections = term
    ? availableConnections.filter(
        (c) => c.label.toLowerCase().includes(term) || c.description.toLowerCase().includes(term),
      )
    : availableConnections;

  const handleSelect = (value: string) => {
    const connection = availableConnections.find((c) => c.id === value);
    if (connection) {
      onSelectConnection(connection);
    }
    setSearch("");
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={searchInputId} className="text-sm">
          {label}
        </Label>
        {labelExtra}
      </div>
      <div className="relative">
        <CommandPrimitive
          shouldFilter={false}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setOpen(false);
              inputRef.current?.blur();
            }
            if ((e.key === "ArrowDown" || e.key === "ArrowRight") && !open) {
              setOpen(true);
            }
          }}
        >
          <div
            className={cn(
              "focus-within:ring-ring focus-within:border-ring flex h-9 items-center gap-2 rounded-md border border-b-[0.5px] py-3 pr-2 pl-3 focus-within:ring-2 focus-within:ring-offset-2",
              open && "rounded-b-none !border-b-0 shadow-md",
            )}
          >
            <CommandPrimitive.Input
              ref={inputRef}
              id={searchInputId}
              data-testid="connection-search-input"
              placeholder={placeholder}
              value={search}
              onValueChange={(value) => {
                setSearch(value);
                if (!open && value.trim()) {
                  setOpen(true);
                }
              }}
              onBlur={() => setOpen(false)}
              onClick={() => {
                if (!open) {
                  setOpen(true);
                }
              }}
              disabled={disabled || isLoading}
              className="placeholder:text-muted-foreground flex h-10 w-full border-0 bg-transparent px-0 text-sm !shadow-none outline-none placeholder:text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            <Button
              variant="ghost"
              size="icon"
              aria-label="Add organization"
              type="button"
              className="[&_svg]:text-muted-foreground size-6 opacity-50 hover:opacity-100 focus:opacity-100"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (!open) {
                  setOpen(true);
                }
                inputRef.current?.focus();
              }}
            >
              <Plus className="size-4" />
            </Button>
          </div>

          {open && (
            <CommandPrimitive.List className="bg-background border-border absolute top-full right-0 left-0 z-50 mt-[-1px] max-h-48 overflow-y-auto rounded-b-md border-[0.5px] shadow-md">
              {isLoading ? (
                <div className="text-muted-foreground p-2 text-sm">Loading external connections...</div>
              ) : filteredConnections.length === 0 ? (
                <CommandPrimitive.Empty className="text-muted-foreground p-2 text-sm">
                  {availableConnections.length === 0
                    ? "No external connections available."
                    : term
                      ? "No matching connections found."
                      : "All organizations have been added."}
                </CommandPrimitive.Empty>
              ) : (
                <div>
                  {filteredConnections.map((connection) => (
                    <CommandPrimitive.Item
                      key={connection.id}
                      value={connection.id}
                      onSelect={handleSelect}
                      onMouseDown={(e) => e.preventDefault()}
                      className="group data-[selected=true]:bg-accent px-2 py-1.5 text-sm outline-hidden"
                    >
                      <div className="flex items-center gap-1.5">
                        <Avatar size="xs">
                          <AvatarFallback
                            size="xs"
                            className="group-data-[selected=true]:bg-background transition-colors duration-0 ease-out group-data-[selected=true]:duration-100"
                          >
                            <Globe className="size-3" />
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                          <span className="text-xs">{connection.label}</span>
                          {connection.description && (
                            <span className="text-muted-foreground text-xs font-light">{connection.description}</span>
                          )}
                        </div>
                      </div>
                    </CommandPrimitive.Item>
                  ))}
                </div>
              )}
            </CommandPrimitive.List>
          )}
        </CommandPrimitive>
      </div>
    </div>
  );
}
